import type { FunctionComponent } from 'react';
import {
  Badge,
  Card,
  Inline,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
  mediaHelper,
  boxShadow,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import {
  ButtonFromAPIWithClickHandler,
  mapDynamicContentItem,
} from '../../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { ResultOf } from 'gql.tada';
import { weblinkFragment } from '../../../../graphql/fragments/weblinkFragment';
import { textFragment } from '../../../../graphql/fragments/textFragment';
import { DynamicContent } from '../../../../app-kit/components/DynamicContent/DynamicContent';
import { mapBadgeVariant } from '../../../../app-kit/helpers/mapBadgeVariant';
import { Stamp, StampType } from './Stamp';
import { badgeFragment } from '../../../../graphql/fragments/badgeFragment';

type WebLinkFragment = ResultOf<typeof weblinkFragment>;
type TextFragment = ResultOf<typeof textFragment>;
type BadgeFragment = ResultOf<typeof badgeFragment>;

export interface OverlayTilesItem {
  title: string;
  badge: BadgeFragment;
  stamp: StampType;
  description: (ButtonFromAPIWithClickHandler | TextFragment | WebLinkFragment)[];
}

interface OverlayTilesProps {
  tiles: OverlayTilesItem[];
}

const TilesWrapper = styled.div`
  display: flex;
  padding-bottom: ${spacingHelper('extraSmall')};
  justify-content: space-between;
  gap: ${spacingHelper('extraSmall')};
  ${mediaHelper('tabletMax')} {
    flex-wrap: wrap;
  }
`;

const CardWrapper = styled.div`
  flex: 1;
  ${mediaHelper('tabletMax')} {
    flex: 0 0 100%;
  }
`;

const StyledCard = styled(Card)`
  ${boxShadow.defaultBoxShadow};
  background-color: ${paletteHelper('backgroundPrimary')};
  padding: ${spacingHelper('medium large small')};
  height: 100%;
`;

const TileHeader = styled(Inline)`
  margin-bottom: ${spacingHelper('medium')};
`;

const TileTitle = styled(Text)`
  ${typographyHelper('body01Bold')};
  color: ${paletteHelper('textPrimary')};
`;

const StyledStamp = styled(Stamp)`
  flex: 0 0 ${spacingHelper('twoExtraLarge')};
`;

const DescriptionWrapper = styled.div`
  line-height: 1.125rem;
  button {
    display: contents;
  }
`;

const Description = styled(DynamicContent)`
  ${typographyHelper('body02')};
`;

export const OverlayTile: FunctionComponent<OverlayTilesItem> = ({
  title,
  badge,
  stamp,
  description,
}) => {
  return (
    <StyledCard borderRadius="medium">
      <TileHeader gap="0" grow={false} justifyContent="space-between">
        <TileTitle>{title}</TileTitle>
        <Badge variant={mapBadgeVariant(badge.variant)}>{badge.text}</Badge>
      </TileHeader>
      <Inline alignItems="flex-start" gap="medium">
        <StyledStamp stamp={stamp} />
        <DescriptionWrapper>
          <Description
            dynamicContents={description
              .map(mapDynamicContentItem)
              .filter((contentItem) => contentItem !== null)}
            textColorVariant="textSecondary"
          />
        </DescriptionWrapper>
      </Inline>
    </StyledCard>
  );
};

export const OverlayTiles: FunctionComponent<OverlayTilesProps> = ({ tiles }) => {
  return (
    <TilesWrapper>
      {tiles.map((tile) => {
        const { title, badge, stamp, description } = tile;
        return (
          <CardWrapper key={title}>
            <OverlayTile title={title} badge={badge} stamp={stamp} description={description} />
          </CardWrapper>
        );
      })}
    </TilesWrapper>
  );
};
